import { Roles } from "../../constants/roles";
import { boardMediaSliderForm } from "./board-media-slider-form";
import { BoardMediaSliderListener } from "./board-media-slider-listener";
import { HorizontalSliderRender } from "../../slider/horizontal-slider-render";

const DESKTOP_MEDIA_QUERY = "(min-width: 769px)";

export class BoardMediaSliderSetup {
    private static readonly areas = new Set<HTMLElement>();

    private static readonly observers = new WeakMap<HTMLElement, MutationObserver>();

    private static resizeBound = false;

    public static init(rootEl?: ParentNode | Document | HTMLElement): void {
        const root = rootEl || document;
        if (!(root instanceof Document || root instanceof HTMLElement)) return;

        const areas: HTMLElement[] = [];
        if (root instanceof HTMLElement && root.matches(`[data-role="${Roles.BOARD_MEDIA_AREA}"]`)) {
            areas.push(root);
        }

        boardMediaSliderForm.getMediaAreas(root).forEach((area) => {
            if (!areas.includes(area)) {
                areas.push(area);
            }
        });

        areas.forEach((area) => {
            this.bindArea(area);
            this.syncArea(area);
            this.areas.add(area);
        });

        if (!this.resizeBound) {
            this.resizeBound = true;
            BoardMediaSliderListener.bindWindowResize(() => {
                this.areas.forEach((area) => this.syncArea(area));
            });
        }
    }

    private static bindArea(area: HTMLElement): void {
        const grid = this.getGrid(area);
        if (!(grid instanceof HTMLElement)) {
            boardMediaSliderForm.getNavButton(area, "prev")?.remove();
            boardMediaSliderForm.getNavButton(area, "next")?.remove();
            boardMediaSliderForm.getDotsContainer(area)?.remove();
            delete area.dataset.boardMediaSliderBound;
            return;
        }

        const isBound = area.dataset.boardMediaSliderBound === "1";
        const existingPrevButton = boardMediaSliderForm.getNavButton(area, "prev");
        const existingNextButton = boardMediaSliderForm.getNavButton(area, "next");

        if (
            isBound &&
            existingPrevButton instanceof HTMLButtonElement &&
            existingNextButton instanceof HTMLButtonElement
        ) {
            return;
        }

        if (grid.dataset.boardMediaSliderLoadBound !== "1") {
            grid.dataset.boardMediaSliderLoadBound = "1";
            BoardMediaSliderListener.bindLoadCapture(grid, () => {
                this.syncArea(area);
            });
        }

        if (!this.observers.has(area)) {
            const observer = BoardMediaSliderListener.observeAreaMutations(area, () => {
                this.bindArea(area);
                this.syncArea(area);
            });
            this.observers.set(area, observer);
        }

        area.dataset.boardMediaSliderBound = "1";
    }

    private static ensureNavButton(area: HTMLElement, direction: "prev" | "next"): HTMLButtonElement {
        const existing = boardMediaSliderForm.getNavButton(area, direction);
        if (existing instanceof HTMLButtonElement) {
            return existing;
        }

        const button = document.createElement("button");
        button.type = "button";
        button.className = `board-media-slider__nav board-media-slider__nav--${direction}`;
        button.setAttribute("aria-label", direction === "prev" ? "Previous image" : "Next image");
        button.innerHTML =
            direction === "prev"
                ? '<i class="bi bi-chevron-left" aria-hidden="true"></i>'
                : '<i class="bi bi-chevron-right" aria-hidden="true"></i>';
        area.appendChild(button);
        return button;
    }

    private static ensureDotsContainer(area: HTMLElement): HTMLElement {
        const existing = boardMediaSliderForm.getDotsContainer(area);
        if (existing instanceof HTMLElement) {
            return existing;
        }

        const dots = document.createElement("div");
        dots.className = "horizontal-slider__dots board-media-slider__dots";
        dots.setAttribute("data-role", Roles.HORIZONTAL_SLIDER_DOTS);
        dots.setAttribute("aria-label", "Slider position");
        const postDetailContentWrapper = boardMediaSliderForm.getPostDetailContentWrapper(area);
        if (postDetailContentWrapper instanceof HTMLElement) {
            postDetailContentWrapper.appendChild(dots);
            return dots;
        }

        area.appendChild(dots);
        return dots;
    }

    private static getGrid(area: HTMLElement): HTMLElement | null {
        return boardMediaSliderForm.getTrack(area);
    }

    private static getSlides(area: HTMLElement): HTMLElement[] {
        const grid = this.getGrid(area);
        if (!(grid instanceof HTMLElement)) {
            return [];
        }

        const itemSlides = boardMediaSliderForm.getSlideItems(grid);
        if (itemSlides.length > 0) {
            return itemSlides;
        }

        return boardMediaSliderForm.getDirectSlides(grid);
    }

    private static getImages(area: HTMLElement): HTMLImageElement[] {
        const grid = this.getGrid(area);
        if (!(grid instanceof HTMLElement)) {
            return [];
        }
        return boardMediaSliderForm.getImages(grid);
    }

    private static isDesktop(): boolean {
        return globalThis.matchMedia(DESKTOP_MEDIA_QUERY).matches;
    }

    private static getStep(area: HTMLElement): number {
        const slides = this.getSlides(area);
        const grid = this.getGrid(area);
        const firstSlide = slides[0];
        if (!(firstSlide instanceof HTMLElement) || !(grid instanceof HTMLElement)) {
            return 0;
        }

        const slideWidth = Math.ceil(firstSlide.getBoundingClientRect().width);
        const style = globalThis.getComputedStyle(grid);
        const gap = Number.parseFloat(style.columnGap || style.gap || "0") || 0;
        return slideWidth + gap;
    }

    private static step(area: HTMLElement, delta: number): void {
        const slides = this.getSlides(area);
        if (!this.isDesktop() || slides.length <= 1) return;

        const maxIndex = Math.max(slides.length - 1, 0);
        const current = Number.parseInt(area.dataset.boardMediaSliderIndex || "0", 10) || 0;
        const next = Math.min(Math.max(current + delta, 0), maxIndex);

        if (next === current) return;

        area.dataset.boardMediaSliderIndex = String(next);
        this.syncArea(area);
    }

    private static syncArea(area: HTMLElement): void {
        const grid = this.getGrid(area);
        const isPostDetailArea = boardMediaSliderForm.getPostDetailPreviewRoot(area) instanceof HTMLElement;
        const slides = this.getSlides(area);
        const enabled = this.isDesktop() && slides.length > 1 && grid instanceof HTMLElement;

        area.classList.toggle("board-media-area--slider-enabled", enabled);

        if (!enabled) {
            area.dataset.boardMediaSliderIndex = "0";

            // On mobile with multiple slides let CSS native touch-scroll handle it.
            // Only lock overflow when a single slide needs no scroll.
            const mobileTouchScroll = !this.isDesktop() && slides.length > 1;

            area.style.overflowX = mobileTouchScroll ? "" : "hidden";
            area.style.touchAction = mobileTouchScroll ? "" : "pan-y";

            const track = boardMediaSliderForm.getTrack(area);
            if (track instanceof HTMLElement) {
                track.style.overflowX = mobileTouchScroll ? "" : "hidden";
                track.style.scrollSnapType = mobileTouchScroll ? "" : "none";
            }
            if (grid instanceof HTMLElement) {
                grid.style.transform = "";
                grid.style.overflowX = mobileTouchScroll ? "" : "hidden";
                grid.style.scrollSnapType = mobileTouchScroll ? "" : "none";
            }

            boardMediaSliderForm.getNavButton(area, "prev")?.remove();
            boardMediaSliderForm.getNavButton(area, "next")?.remove();
            boardMediaSliderForm.getDotsContainer(area)?.remove();
            return;
        }

        // Reset inline overrides when desktop slider takes over
        area.style.overflowX = "";
        area.style.touchAction = "";

        if (!(grid instanceof HTMLElement)) {
            return;
        }

        const prevButton = this.ensureNavButton(area, "prev");
        const nextButton = this.ensureNavButton(area, "next");
        const dotsContainer = isPostDetailArea ? this.ensureDotsContainer(area) : null;

        if (prevButton.dataset.boardMediaSliderClickBound !== "1") {
            prevButton.dataset.boardMediaSliderClickBound = "1";
            BoardMediaSliderListener.bindClick(prevButton, () => this.step(area, -1));
        }

        if (nextButton.dataset.boardMediaSliderClickBound !== "1") {
            nextButton.dataset.boardMediaSliderClickBound = "1";
            BoardMediaSliderListener.bindClick(nextButton, () => this.step(area, 1));
        }

        if (dotsContainer instanceof HTMLElement) {
            dotsContainer.hidden = false;
            HorizontalSliderRender.ensureDots(dotsContainer, slides.length);
            boardMediaSliderForm.getDots(dotsContainer).forEach((dot) => {
                if (dot.dataset.boardMediaSliderClickBound === "1") {
                    return;
                }

                dot.dataset.boardMediaSliderClickBound = "1";
                BoardMediaSliderListener.bindClick(dot, () => {
                    const index = Number.parseInt(dot.dataset.index || "0", 10) || 0;
                    area.dataset.boardMediaSliderIndex = String(index);
                    this.syncArea(area);
                });
            });
        }

        const maxIndex = Math.max(slides.length - 1, 0);
        const current = Number.parseInt(area.dataset.boardMediaSliderIndex || "0", 10) || 0;
        const index = Math.min(Math.max(current, 0), maxIndex);
        const step = this.getStep(area);
        area.dataset.boardMediaSliderIndex = String(index);
        grid.style.transform = `translateX(${-index * step}px)`;
        prevButton.disabled = index <= 0;
        nextButton.disabled = index >= maxIndex;
        if (dotsContainer instanceof HTMLElement) {
            HorizontalSliderRender.renderActiveDot(dotsContainer, index);
            HorizontalSliderRender.renderDotsInteractivity(dotsContainer, false);
        }
    }
}
